import { _notNil } from '@/littledash';
import { MessagingService, SseConnection } from '@/support/MessagingService';
import { useCallback, useEffect, useRef, useState } from 'react';

export type SseStreamStatus = 'connecting' | 'connected' | 'disconnected';

export interface UseSseStreamOutput<T> {
  status: SseStreamStatus;
  isConnected: boolean;
  lastEvent: T | null;
  connect: () => void;
  disconnect: () => void;
}

const MAX_RECONNECT_DELAY_MS = 30_000;
const INITIAL_RECONNECT_DELAY_MS = 1_000;

/**
 * Generic hook that manages the lifecycle of an SSE connection to the messaging service.
 * Handles connect/disconnect, reconnection with exponential backoff, and typed event delivery.
 *
 * The hook takes full ownership of reconnection timing, the browser's built-in EventSource
 * retry is disabled by closing the connection on every error and rescheduling via backoff.
 */
export const useSseStream = <T>(path: string): UseSseStreamOutput<T> => {
  const [status, setStatus] = useState<SseStreamStatus>('disconnected');
  const [lastEvent, setLastEvent] = useState<T | null>(null);
  const connectionRef = useRef<SseConnection | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectDelayRef = useRef(INITIAL_RECONNECT_DELAY_MS);
  const shouldReconnectRef = useRef(false);
  const openConnectionRef = useRef<() => void>(() => {});

  const clearReconnectTimeout = useCallback(() => {
    if (_notNil(reconnectTimeoutRef.current)) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  const closeConnection = useCallback(() => {
    if (_notNil(connectionRef.current)) {
      connectionRef.current.close();
      connectionRef.current = null;
    }
  }, []);

  const openConnection = useCallback(() => {
    closeConnection();
    clearReconnectTimeout();
    setStatus('connecting');
    setLastEvent(null);

    const connection = MessagingService.createSseConnection<T>(path, {
      onEvent: (data) => {
        setLastEvent(data);
      },
      onConnect: () => {
        setStatus('connected');
        reconnectDelayRef.current = INITIAL_RECONNECT_DELAY_MS;
      },
      onError: () => {
        setStatus('disconnected');
        connectionRef.current = null;

        if (shouldReconnectRef.current) {
          const delay = reconnectDelayRef.current;
          reconnectDelayRef.current = Math.min(delay * 2, MAX_RECONNECT_DELAY_MS);
          reconnectTimeoutRef.current = setTimeout(() => {
            if (shouldReconnectRef.current) {
              openConnectionRef.current();
            }
          }, delay);
        }
      },
    });

    connectionRef.current = connection;
  }, [path, closeConnection, clearReconnectTimeout]);

  useEffect(() => {
    openConnectionRef.current = openConnection;
  }, [openConnection]);

  const connect = useCallback(() => {
    shouldReconnectRef.current = true;
    reconnectDelayRef.current = INITIAL_RECONNECT_DELAY_MS;
    openConnection();
  }, [openConnection]);

  const disconnect = useCallback(() => {
    shouldReconnectRef.current = false;
    clearReconnectTimeout();
    closeConnection();
    setStatus('disconnected');
    setLastEvent(null);
  }, [clearReconnectTimeout, closeConnection]);

  useEffect(() => {
    connect();
    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  return { status, isConnected: status === 'connected', lastEvent, connect, disconnect };
};
